import { serverHost } from "variables/serverdata";

// -----------------------
// Auth helper
// -----------------------
function getAuthToken() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return localStorage.getItem("token");
    const parsed = JSON.parse(raw);
    return (
      parsed?.access_token ||
      parsed?.token ||
      parsed?.accessToken ||
      null
    );
  } catch {
    return localStorage.getItem("token");
  }
}

// Fetch a single device by UUID
export const getDevice = async (device_uuid) => {
  const res = await fetch(`${serverHost}/get_device/${device_uuid}`);
  if (!res.ok) throw new Error("Failed to fetch device");
  return res.json();
};

// Create a new device
export const createDevice = async (payload) => {
  if (!payload.device) payload.device = {};

  const res = await fetch(`${serverHost}/create_device`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create device: ${text}`);
  }

  return res.json();
};


// Update existing device
export const updateDevice = async (device_uuid, { device, lines = [], keys = [], settings = [] }) => {
  const res = await fetch(`${serverHost}/update_device/${device_uuid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ device, lines, keys, settings }),
  });
  if (!res.ok) throw new Error("Failed to update device");
  return res.json();
};

// Delete device
export const deleteDevice = async (device_uuid) => {
  const res = await fetch(`${serverHost}/delete_device/${device_uuid}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete device");
  return res.json();
};

export const getDeviceProfile = async (device_profile_uuid) => {
  const res = await fetch(`${serverHost}/get_device_profile/${device_profile_uuid}`);
  if (!res.ok) throw new Error("Failed to fetch device profile");
  return res.json();
};

// Create a new device profile
export const createDeviceProfile = async ({ profile, keys = [], settings = [] }, domain_uuid, user_uuid) => {

  const res = await fetch(`${serverHost}/create_device_profile`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profile, keys, settings, domain_uuid, user_uuid }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create device profile: ${text}`);
  }

  return res.json();
};

// Update existing device profile
export const updateDeviceProfile = async (device_profile_uuid, { profile, keys = [], settings = [] }, domain_uuid, user_uuid) => {
  const res = await fetch(`${serverHost}/update_device_profile/${device_profile_uuid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ profile, keys, settings, domain_uuid, user_uuid }),
  });

  if (!res.ok) throw new Error("Failed to update device profile");
  return res.json();
};

// Delete device profile
export const deleteDeviceProfile = async (device_profile_uuid) => {
  const res = await fetch(`${serverHost}/delete_device_profile/${device_profile_uuid}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete device profile");
  return res.json();
};

// Fetch a single device vendor by UUID
export const getDeviceVendor = async (device_vendor_uuid) => {
  const res = await fetch(`${serverHost}/get_device_vendor/${device_vendor_uuid}`);
  if (!res.ok) throw new Error("Failed to fetch device vendor");
  return res.json();
};

// Fetch vendor functions by vendor UUID
export const getVendorFunctions = async (device_vendor_uuid) => {
  if (!device_vendor_uuid) throw new Error("Vendor UUID is required");
  const res = await fetch(
    `${serverHost}/get_vendor_functions/${device_vendor_uuid}`
  );
  if (!res.ok) throw new Error("Failed to fetch vendor functions");
  return res.json();
};

export const createDeviceVendor = async (vendor, user_uuid) => {
  const res = await fetch(`${serverHost}/create_device_vendor`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...vendor,
      user_uuid,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create device vendor: ${text}`);
  }

  return res.json();
};

// Update an existing device vendor
export const updateDeviceVendor = async (device_vendor_uuid, vendor, user_uuid) => {
  const res = await fetch(
    `${serverHost}/update_device_vendor/${device_vendor_uuid}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...vendor,
        enabled: vendor.enabled === true || vendor.enabled === "true",
        user_uuid,
      }),
    }
  );

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to update device vendor: ${text}`);
  }

  return res.json();
};

// Delete a device vendor
export const deleteDeviceVendor = async (device_vendor_uuid) => {
  const res = await fetch(`${serverHost}/delete_device_vendor/${device_vendor_uuid}`, {
    method: "DELETE",
  });

  if (!res.ok) throw new Error("Failed to delete device vendor");
  return res.json();
};

// Create a new vendor function
export const createVendorFunction = async (device_vendor_uuid, fn) => {
  if (!device_vendor_uuid) throw new Error("Vendor UUID is required");

  const payload = { ...fn, device_vendor_uuid };

  const res = await fetch(`${serverHost}/create_vendor_function`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create vendor function: ${text}`);
  }

  return res.json();
};

// Update an existing vendor function
export const updateVendorFunction = async (device_vendor_function_uuid, fn) => {
  if (!device_vendor_function_uuid) throw new Error("Function UUID is required");

  const res = await fetch(`${serverHost}/update_vendor_function/${device_vendor_function_uuid}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fn),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to update vendor function: ${text}`);
  }

  return res.json();
};

// -----------------------
// Toggle Devices (Bulk)
// -----------------------
export async function toggleDevices(deviceUuids) {
  if (!Array.isArray(deviceUuids) || deviceUuids.length === 0) {
    throw new Error("deviceUuids must be a non-empty array");
  }

  const token = getAuthToken();
  if (!token) throw new Error("Missing auth token");

  const res = await fetch(`${serverHost}/toggle_devices`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ device_uuids: deviceUuids }),
  });

  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "Failed to toggle devices");
  }

  return data;
}

export const getDevicesSummary = async (siteUuid) => {
  const token = getAuthToken();
  if (!siteUuid) throw new Error("No site selected");

  const res = await fetch(`${serverHost}/dashboard/devices_summary`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "x-site-uuid": siteUuid,
    },
  });

  if (!res.ok) {
    throw new Error("Failed to fetch devices summary");
  }

  return await res.json();
};